/**
 * Capacity / bandwidth helpers for link route visualisation.
 *
 * Source of truth is the Supabase column `Grouping_BW`, which already buckets
 * each link into a bandwidth band (< 200, 200-400, 400-800, 800-1600,
 * 1600-3200, > 3200). Numeric columns are used only as a fallback when the
 * grouping value is missing.
 */

/** Column that carries the pre-computed bandwidth grouping. */
const BW_GROUP_FIELDS = [
  'Grouping_BW',
  'grouping_bw',
  'GROUPING_BW',
  'grouping_BW',
]

/** Raw numeric bandwidth columns, used only as a fallback. */
const NUMERIC_FIELDS = [
  'capacity',
  'kapasitas',
  'bw',
  'bandwidth',
  'capacity_mbps',
  'link_capacity',
]

export interface CapacityTier {
  id: string
  label: string
  color: string
  weight: number
  min: number
  max: number
}

/**
 * Ordered highest → lowest so the legend reads top-down from fastest to
 * slowest, and classification can pick the first tier whose `min` is met.
 */
export const CAPACITY_TIERS: CapacityTier[] = [
  {
    id: 'gt3200',
    label: '> 3200',
    color: '#22d3ee', // cyan — highest
    weight: 5.5,
    min: 3200,
    max: Infinity,
  },
  {
    id: '1600-3200',
    label: '1600 – 3200',
    color: '#3b82f6', // blue
    weight: 4.6,
    min: 1600,
    max: 3200,
  },
  {
    id: '800-1600',
    label: '800 – 1600',
    color: '#10b981', // emerald
    weight: 3.8,
    min: 800,
    max: 1600,
  },
  {
    id: '400-800',
    label: '400 – 800',
    color: '#f59e0b', // amber
    weight: 3.1,
    min: 400,
    max: 800,
  },
  {
    id: '200-400',
    label: '200 – 400',
    color: '#f97316', // orange
    weight: 2.6,
    min: 200,
    max: 400,
  },
  {
    id: 'lt200',
    label: '< 200',
    color: '#ef4444', // red — lowest
    weight: 2.2,
    min: 0,
    max: 200,
  },
]

export const UNKNOWN_TIER: CapacityTier = {
  id: 'unknown',
  label: 'Unknown',
  color: '#64748b',
  weight: 2,
  min: -1,
  max: -1,
}

/** Raw `Grouping_BW` text as stored (e.g. "400-800", "> 3200"). */
export function getBwGroupRaw(row: any): string {
  if (!row) return ''
  for (const field of BW_GROUP_FIELDS) {
    const val = row[field]
    if (val !== null && val !== undefined && String(val).trim() !== '') {
      return String(val).trim()
    }
  }
  return ''
}

/** Numeric bandwidth from a raw numeric column, when available. */
export function getCapacityValue(row: any): number | null {
  if (!row) return null

  for (const field of NUMERIC_FIELDS) {
    const val = row[field]
    if (val === null || val === undefined || String(val).trim() === '') continue

    const raw = String(val).trim()

    // "2x400" → 800
    const multi = raw.match(/(\d+)\s*[x×]\s*(\d[\d.,]*)/i)
    if (multi) {
      const count = parseFloat(multi[1])
      const each = parseFloat(multi[2].replace(/,/g, ''))
      if (!isNaN(count) && !isNaN(each)) return count * each
    }

    const single = raw.match(/(\d[\d.,]*)/)
    if (single) {
      const num = parseFloat(single[1].replace(/,/g, ''))
      if (!isNaN(num)) return num
    }
  }

  return null
}

/** Pick the tier that contains a numeric bandwidth value. */
export function classifyValue(value: number): CapacityTier {
  return CAPACITY_TIERS.find((t) => value >= t.min) ?? UNKNOWN_TIER
}

/**
 * Resolve a tier from the `Grouping_BW` label. Handles the numeric band
 * formats plus "<"/">" and Indonesian "dibawah"/"diatas" phrasing.
 */
function tierFromGroupLabel(raw: string): CapacityTier | null {
  if (!raw) return null

  const text = raw.toLowerCase()

  // Direct id/label match first (cheap and exact).
  const direct = CAPACITY_TIERS.find(
    (t) =>
      text.replace(/\s/g, '') === t.label.toLowerCase().replace(/[\s–-]/g, '') ||
      text.replace(/\s/g, '') === t.id.toLowerCase(),
  )
  if (direct) return direct

  const numbers = (text.match(/\d[\d.,]*/g) || []).map((n) =>
    parseFloat(n.replace(/,/g, '')),
  )
  if (!numbers.length) return null

  const isBelow = /[<]|bawah|below|under|kurang/.test(text)
  const isAbove = /[>]|atas|above|over|lebih|\+/.test(text)

  if (numbers.length >= 2) {
    const lower = Math.min(numbers[0], numbers[1])
    const upper = Math.max(numbers[0], numbers[1])
    return classifyValue((lower + upper) / 2)
  }

  const n = numbers[0]
  if (isBelow) return classifyValue(Math.max(0, n - 1))
  if (isAbove) return classifyValue(n + 1)
  return classifyValue(n)
}

/** Colour + stroke weight for a link, driven by `Grouping_BW`. */
export function getCapacityTier(row: any): CapacityTier {
  const grouped = tierFromGroupLabel(getBwGroupRaw(row))
  if (grouped) return grouped

  const numeric = getCapacityValue(row)
  if (numeric !== null) return classifyValue(numeric)

  return UNKNOWN_TIER
}

/** Short display label — prefers the stored grouping text. */
export function getCapacityLabel(row: any): string {
  const grouped = getBwGroupRaw(row)
  if (grouped) return /mbps|gbps/i.test(grouped) ? grouped : `${grouped} Mbps`

  const numeric = getCapacityValue(row)
  if (numeric !== null) return `${numeric.toLocaleString()} Mbps`

  return '—'
}

/** Count how many rows fall into each tier (highest → lowest). */
export function getTierDistribution(rows: any[]) {
  return CAPACITY_TIERS.map((tier) => ({
    tier,
    count: rows.filter((r) => getCapacityTier(r).id === tier.id).length,
  })).filter((entry) => entry.count > 0)
}
